import gql from "graphql-tag";
import { isArray } from "lodash";
import { computed, nextTick } from "vue";
import { useStore } from "vuex";
import { STATIC_API_TTL_MS } from "../../../../store/common/api-handler/api-handler";

const CATEGORIES_LIST_QUERY = {
  query: gql`
    query {
      categoriesList(page: 1, size: 10, parent: 0) {
        content {
          id
          name
          keyword
          url(url: "/store/category/_id")
          categories {
            id
            name
            keyword
            url(url: "/store/category/_id")
            image
          }
        }
      }
      manufacturerList(page: 1, size: -1, sort: "name", order: "ASC") {
        content {
          id
          name
          url(url: "/store/manufacturer/_id")
        }
      }
    }
  `,
};

// NOTE (perf audit 2026-07-02, F1): the menu-click catalog prefetch that lived
// here (CATALOG_PREFETCH_QUERY + prefetchCatalogData) was removed. Its result
// (window.__vf_catalog_prefetch) was never consumed by category.vue, so every
// menu navigation fired a duplicate ~1s productsList query that competed with
// the real catalog fetch for backend connections.

export function useHeaderMenuData() {
  const store = useStore();

  const key = "headlineCouponData"; // Unique key for this component's API call
  const menuAPIkey = "categoriesList";

  const isMenuApiLoading = computed(() =>
    store.getters["apiHandler/getIsLoading"](menuAPIkey)
  );

  const categories = computed(
    () =>
      store.getters["apiHandler/getApiData"](menuAPIkey)?.categoriesList
        ?.content || []
  );

  const manufacturers = computed(
    () =>
      store.getters["apiHandler/getApiData"](menuAPIkey)?.manufacturerList
        ?.content || []
  );

  const shapesKey = "shapesDataHomePage";
  const colorsKey = "colorsMenuList";
  const featuresKey = "featuresMenuList";
  const filtersKey = "menuFiltersList";

  const shapesData = computed(
    () => store.getters["apiHandler/getApiData"](shapesKey)?.shapes || []
  );
  const colorsData = computed(
    () => store.getters["apiHandler/getApiData"](colorsKey)?.colors || []
  );
  const featuresData = computed(
    () => store.getters["apiHandler/getApiData"](featuresKey)?.features || []
  );
  const allFilterGroups = computed(() => {
    const raw =
      store.getters["apiHandler/getApiData"](filtersKey)?.filter_groups || [];
    return Array.isArray(raw) ? raw : Object.values(raw);
  });

  const couponData = computed(
    () => store.getters["apiHandler/getApiData"](key)?.coupon || []
  );

  const isCouponValid = computed(() => {
    if (
      !couponData.value ||
      (isArray(couponData) && couponData.value.length === 0)
    )
      return false;

    const currentDate = new Date();
    const couponEndDate = new Date(couponData.value.date_end);

    return currentDate < couponEndDate;
  });

  const fetchCoupon = async (onFetched?: () => void) => {
    await store.dispatch("apiHandler/fetchApi", {
      key,
      params: { route: "api/coupon/publicCoupon" },
    });

    await nextTick();
    onFetched?.();
  };

  // Catalog structure changes rarely — persist these responses in
  // sessionStorage so full reloads / restored tabs render the menu without
  // refetching (~35KB and 5 requests saved per load).
  const STATIC_MENU_TTL_MS = STATIC_API_TTL_MS;

  const fetchGraphQLAPIfun = async () => {
    store.dispatch("apiHandler/fetchGraphQL", {
      key: menuAPIkey,
      query: CATEGORIES_LIST_QUERY,
      persistTtlMs: STATIC_MENU_TTL_MS,
    });
  };

  const fetchMenuFilters = () => {
    store.dispatch("apiHandler/fetchApi", {
      key: shapesKey,
      params: { route: "api/shapes" },
      persistTtlMs: STATIC_MENU_TTL_MS,
    });
    store.dispatch("apiHandler/fetchApi", {
      key: colorsKey,
      params: { route: "api/colors" },
      persistTtlMs: STATIC_MENU_TTL_MS,
    });
    store.dispatch("apiHandler/fetchApi", {
      key: featuresKey,
      params: { route: "api/features" },
      persistTtlMs: STATIC_MENU_TTL_MS,
    });
    store.dispatch("apiHandler/fetchApi", {
      key: filtersKey,
      params: { route: "api/filters", new: true },
      persistTtlMs: STATIC_MENU_TTL_MS,
    });
  };

  return {
    isMenuApiLoading,
    categories,
    manufacturers,
    shapesData,
    colorsData,
    featuresData,
    allFilterGroups,
    couponData,
    isCouponValid,
    fetchCoupon,
    fetchGraphQLAPIfun,
    fetchMenuFilters,
  };
}
